import { MailIcon } from 'lucide-react';
import { CONTROL_SIZE } from '@/components/ui/types/control-enums';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Control, FieldPath, useController } from 'react-hook-form';

import type { RegistrationFormData } from '@/features/signup/types/use-registration-form-types';
import { Link } from 'react-router-dom';

import { INPUT_APPEARANCE } from '@/components/ui/types/input-variants-enums';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

enum EMAIL_CHECK_STATUS {
  IDLE = 'idle',
  CHECKING = 'checking',
  AVAILABLE = 'available',
  TAKEN = 'taken',
}

interface SignupEmailFieldProps {
  control: Control<RegistrationFormData>;
  name: FieldPath<RegistrationFormData>;
  onAvailabilityChange?: (available: boolean) => void;
}

const emailCache = new Map<string, boolean>();

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

async function checkEmailAvailability(
  email: string,
  signal: AbortSignal
): Promise<boolean> {
  const cached = emailCache.get(email);
  if (cached !== undefined) {
    return cached;
  }

  const response = await fetch(
    `${import.meta.env.VITE_APP_API_BASE_URL}/auth/check-email`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      signal,
    }
  );

  if (!response.ok) {
    throw new Error(`Email check failed: ${response.status}`);
  }

  const data = await response.json();
  const available = data.available as boolean;
  emailCache.set(email, available);
  return available;
}

export function SignupEmailField({
  control,
  name,
  onAvailabilityChange,
}: SignupEmailFieldProps) {
  const [emailCheckStatus, setEmailCheckStatus] = useState<EMAIL_CHECK_STATUS>(EMAIL_CHECK_STATUS.IDLE);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: {
      required: 'Email is required',
      pattern: {
        value: EMAIL_PATTERN,
        message: 'Invalid email address',
      },
    },
  });

  const onAvailabilityChangeRef = useRef(onAvailabilityChange);
  const fieldOnBlurRef = useRef(field.onBlur);
  const fieldValueRef = useRef(field.value);

  // Latest-callback / field mirror: update in layout effect (not during render)
  useLayoutEffect(() => {
    onAvailabilityChangeRef.current = onAvailabilityChange;
    fieldOnBlurRef.current = field.onBlur;
    fieldValueRef.current = field.value;
  });

  const handleBlur = useCallback(async () => {
    fieldOnBlurRef.current();

    const email = String(fieldValueRef.current ?? '')
      .trim()
      .toLowerCase();
    if (!email || !EMAIL_PATTERN.test(email)) {
      return;
    }

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setEmailCheckStatus(EMAIL_CHECK_STATUS.CHECKING);

    try {
      const available = await checkEmailAvailability(email, abortController.signal);
      if (abortController.signal.aborted) {
        return;
      }

      const status: EMAIL_CHECK_STATUS = available ? EMAIL_CHECK_STATUS.AVAILABLE : EMAIL_CHECK_STATUS.TAKEN;
      setEmailCheckStatus(status);
      onAvailabilityChangeRef.current?.(available);
    } catch {
      if (abortController.signal.aborted) {
        return;
      }
      setEmailCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    }
  }, []);


  const emailValue = (field.value as string) ?? '';
  const encodedEmail = encodeURIComponent(emailValue.trim().toLowerCase());
  const errorId = `${name}-error`;
  const statusId = `${name}-status`;
  const describedBy = [
    error ? errorId : undefined,
    emailCheckStatus === 'taken' && !error ? statusId : undefined,
    emailCheckStatus === 'checking' ? statusId : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const statusMessage =
    emailCheckStatus === 'checking'
      ? 'Checking email availability'
      : emailCheckStatus === 'available'
        ? 'Email is available'
        : emailCheckStatus === 'taken'
          ? 'This email is already registered'
          : '';

  return (
    <div className="space-y-1">
      <Input
        appearance={INPUT_APPEARANCE.DEFAULT}
        size={CONTROL_SIZE.COMFORTABLE}
        id={name}
        placeholder="Email address"
        aria-label="Email address"
        aria-invalid={error || emailCheckStatus === 'taken' ? true : undefined}
        aria-describedby={describedBy || undefined}
        type="email"
        autoComplete="email"
        spellCheck={false}
        leftIcon={<MailIcon className="h-4 w-4" aria-hidden="true" />}
        rightIcon={
          emailCheckStatus === 'checking' ? (
            <Spinner size="small" className="h-4 w-4" />
          ) : undefined
        }
        {...field}
        onBlur={() => { void handleBlur(); }}
      />
      <span id={statusId} className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </span>
      {error ? (
        <span
          id={errorId}
          className="type-field-description text-status-danger-foreground"
          role="alert"
        >
          {error.message}
        </span>
      ) : null}
      {emailCheckStatus === 'taken' && !error ? (
        <p className="type-field-description text-status-danger-foreground" role="alert">
          This email is already registered.{' '}
          <Link
            to={`/login?returnUrl=/signup/continue&email=${encodedEmail}`}
            className="font-semibold text-primary hover:underline"
          >
            Sign in instead →
          </Link>
        </p>
      ) : null}
    </div>
  );
}
